import { Injectable, signal } from '@angular/core';
import { FUNCTIONS_BASE, IS_NATIVE } from '../env';
import { APP_VERSION } from '../version';

export interface DiagEntry {
  at: string;
  message: string;
  context: Record<string, unknown>;
}

/**
 * Native-build diagnostics. Store shells have no console we can read, so
 * auth and API failures are (a) kept in memory for the account sheet's
 * diagnostics line and (b) posted to the clientLog function, which writes
 * them to Cloud Logging. Web builds keep them in memory only. Never includes
 * plates, photos, emails or free text - codes and statuses only.
 */
@Injectable({ providedIn: 'root' })
export class DiagService {
  readonly entries = signal<DiagEntry[]>([]);
  private uid = '';

  setUid(uid: string | null): void {
    this.uid = uid ?? '';
  }

  report(message: string, context: Record<string, unknown> = {}, level: 'warn' | 'error' = 'warn'): void {
    const entry: DiagEntry = { at: new Date().toISOString(), message, context };
    this.entries.update((list) => [entry, ...list].slice(0, 20));
    if (!IS_NATIVE) return;
    try {
      void fetch(`${FUNCTIONS_BASE}/clientLog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ level, message, context, platform: 'ios', build: APP_VERSION, uid: this.uid }),
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      /* diagnostics never throw */
    }
  }
}

export type ErrorInfo = Record<string, unknown> & {
  code: string;
  name: string;
  status: string | number;
  message: string;
};

/** Error -> loggable shape (code + clipped message), never the stack. */
export function describeError(e: unknown): ErrorInfo {
  const err = e as { code?: string; message?: string; name?: string; status?: number };
  return {
    code: err?.code ?? '',
    name: err?.name ?? '',
    status: err?.status ?? '',
    message: String(err?.message ?? e ?? '').slice(0, 300),
  };
}
